// A fraction model (14 Oct 2026): a bar, a circle or a rectangle cut into
// equal parts with some of them shaded. For a mixed number there are several
// equal shapes: every one but the last is fully shaded, and `lastShaded`
// parts of the last one are.
//
// Pure parsing only, no components (see practice-diagram-data.ts).

export type FractionModelSpec = {
  type: 'fraction_model';
  shape: 'bar' | 'circle' | 'rectangle';
  // Equal parts in each shape.
  parts: number;
  // Shaded parts in each shape, left to right. One entry for each shape.
  shaded: number[];
};

// More parts than this cannot be counted on a phone.
const MAX_PARTS = 30;
const MAX_SHAPES = 6;

function readCount(value: unknown, min: number, max: number): number | null {
  return typeof value === 'number' &&
    Number.isInteger(value) &&
    value >= min &&
    value <= max
    ? value
    : null;
}

export function readFractionModel(
  record: Record<string, unknown>,
): FractionModelSpec | null {
  const { shape } = record;
  if (shape !== 'bar' && shape !== 'circle' && shape !== 'rectangle')
    return null;
  const parts = readCount(record.parts, 2, MAX_PARTS);
  if (parts === null) return null;
  const shaded = readCount(record.shaded, 0, parts);
  if (shaded === null) return null;
  if (record.wholes === undefined && record.lastShaded === undefined)
    return { type: 'fraction_model', shape, parts, shaded: [shaded] };
  // Several shapes: all but the last are full, so `shaded` must say so.
  const wholes = readCount(record.wholes, 2, MAX_SHAPES);
  const lastShaded = readCount(record.lastShaded, 0, parts);
  if (wholes === null || lastShaded === null || shaded !== parts) return null;
  return {
    type: 'fraction_model',
    shape,
    parts,
    shaded: [...Array<number>(wholes - 1).fill(parts), lastShaded],
  };
}

// A rectangle's parts as the most nearly square grid of whole rows: 12 is
// 3 rows of 4. A number with no such grid (5, 7) is one row.
export function fractionModelGrid(parts: number): {
  rows: number;
  columns: number;
} {
  let rows = 1;
  for (let candidate = 2; candidate * candidate <= parts; candidate += 1)
    if (parts % candidate === 0) rows = candidate;
  return { rows, columns: parts / rows };
}

const SHAPE_WORDS: Record<FractionModelSpec['shape'], [string, string]> = {
  bar: ['bar', 'bars'],
  circle: ['circle', 'circles'],
  rectangle: ['rectangle', 'rectangles'],
};

function partsWord(count: number): string {
  return count === 1 ? '1 part' : `${String(count)} parts`;
}

// What a screen reader says. It counts parts and never names the fraction,
// which is usually the answer.
export function describeFractionModel(model: FractionModelSpec): string {
  const [one, many] = SHAPE_WORDS[model.shape];
  const parts = String(model.parts);
  const last = model.shaded[model.shaded.length - 1] ?? 0;
  if (model.shaded.length === 1) {
    const shaded =
      last === 0
        ? 'No parts are shaded.'
        : last === 1
          ? '1 part is shaded.'
          : `${String(last)} parts are shaded.`;
    return `A ${one} split into ${parts} equal parts. ${shaded}`;
  }
  const full = model.shaded.length - 1;
  const fullWords =
    full === 1
      ? `1 ${one} is fully shaded`
      : `${String(full)} ${many} are fully shaded`;
  const lastWords =
    last === 0
      ? `no parts of the last ${one} are shaded`
      : last === model.parts
        ? `the last ${one} is fully shaded too`
        : `${partsWord(last)} of the last ${one} ${last === 1 ? 'is' : 'are'} shaded`;
  return `${String(model.shaded.length)} ${many}, each split into ${parts} equal parts. ${fullWords} and ${lastWords}.`;
}
